/**
 * hook-a-duck.js — GDD 6.7.1.
 *
 * A circular water channel. Rubber ducks drift round it on a 0.3 m/s current,
 * bobbing gently. The player lifts a fishing rod off its holder and lowers the
 * hook onto the ring on a duck's head: get the hook inside the ring and lift,
 * and the duck comes up with it.
 *
 * Two deliberate implementation choices:
 *
 * 1. **The ducks are kinematic, not floating.** Buoyancy on nine bodies is
 *    expensive, drifts out of formation, and makes the catch test depend on the
 *    solver. Their position along the ring is integrated here, so a catch is
 *    exactly reproducible from a seed.
 *
 * 2. **The line is a Verlet chain, not a physics joint.** Three links of
 *    distance relaxation cost a few dozen flops and never explode; a rope of
 *    impulse joints hanging off a hand-driven kinematic body is the classic way
 *    to make Rapier misbehave. The chain is integrated in stall-LOCAL space —
 *    the plot rotation is a pure yaw, so gravity is still -Y there — which
 *    keeps every catch comparison in one space with no per-duck conversions.
 *
 * Fish are the point of this stall (GDD 7.4): a round always pays at least one,
 * whatever the score, with a bonus fish per three ducks caught.
 *
 * Tuning lives in constants.js HOOK_A_DUCK.
 *
 * @module stalls/hook-a-duck
 */

import * as THREE from 'three';
import {
  HOOK_A_DUCK, PRIZE_KIND, GROUP, makeGroups, DEFAULT_GRAVITY, EVENTS, PALETTE,
} from '../constants.js';
import { StallBase, registerStall } from './framework.js';
import * as factory from '../assets3d/factory.js';
import { createStallLight } from '../world/lighting.js';
import { grab } from '../core/grab.js';
import { bus } from '../core/events.js';
import { haptics } from '../core/haptics.js';
import { rng, randRange } from '../core/testmode.js';

const CFG = HOOK_A_DUCK;

/** Ducks and the hook are logic-only; nothing here should push a rigid body. */
const GHOST = makeGroups(GROUP.SENSOR, 0);

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();

/** Memoised like the factory's own geometry — one canvas per session. */
let _waterTexture = null;

/**
 * Streaked disc that reads as a current the moment the water starts turning.
 * @returns {THREE.CanvasTexture}
 */
function waterTexture() {
  if (_waterTexture) return _waterTexture;
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 256;
  const g = c.getContext('2d');
  g.fillStyle = '#2c7ba6';
  g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 48; i++) {
    const a = (i / 48) * Math.PI * 2;
    g.strokeStyle = i % 2 ? 'rgba(226,246,255,0.16)' : 'rgba(12,58,88,0.2)';
    g.lineWidth = 4 + (i % 3) * 3;
    g.beginPath();
    g.arc(128, 128, 34 + (i % 5) * 19, a, a + 0.55);
    g.stroke();
  }
  _waterTexture = new THREE.CanvasTexture(c);
  return _waterTexture;
}

class HookADuck extends StallBase {
  constructor(ctx) {
    super(ctx);
    /** @type {Array<object>} Live ducks, each with its own ring state. */
    this.ducks = [];
    /** @type {Array<{pos: THREE.Vector3, prev: THREE.Vector3}>} Line nodes, local space. */
    this.chain = [];
    /** @type {Array<(dt: number) => boolean>} Transient visual effects. */
    this._effects = [];
    /** @type {Array<{dispose: () => void}>} Anything allocated by hand. */
    this._disposables = [];

    this.catches = 0;
    this.fishAwarded = [];

    this.rod = null;
    this.rodMesh = null;
    this._rodReturn = 0;
    this._elapsed = 0;
    /** Smoothed upward speed of the rod tip — the "am I lifting?" signal. */
    this._tipVelY = 0;
    this._prevTip = null;
    this.water = null;
  }

  // ── Static build ────────────────────────────────────────────────────────

  /**
   * Non-physical scenery, tracked so teardown can free it. `onBuild` runs again
   * on every round, so anything allocated here and not released leaks a copy
   * per play.
   * @returns {THREE.Mesh}
   * @private
   */
  _decor(geometry, material, x, y, z) {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, y, z);
    this.root.add(mesh);
    this._disposables.push(geometry);
    return mesh;
  }

  onBuild() {
    const c = CFG.ringCentre;
    const outer = CFG.ringRadius + CFG.channelWidth / 2;
    const inner = CFG.ringRadius - CFG.channelWidth / 2;

    // Basin: an outer drum, an island in the middle, and a water annulus
    // between them. Only the plinth carries a collider — the ducks never touch
    // it and a dropped rod just needs somewhere to land.
    const drum = this._decor(
      new THREE.CylinderGeometry(outer + 0.04, outer + 0.04, 0.3, 28, 1, true),
      factory.mat(0x2f4858, { rough: 0.7 }),
      c.x, c.y - 0.13, c.z,
    );
    drum.renderOrder = -1;

    this._decor(
      new THREE.CircleGeometry(outer + 0.04, 28),
      factory.mat(0x143244, { rough: 0.9 }),
      c.x, c.y - 0.26, c.z,
    ).rotation.x = -Math.PI / 2;

    this._decor(
      new THREE.CylinderGeometry(inner, inner, 0.26, 24),
      factory.mat(0x3f6f4f, { rough: 0.95 }),
      c.x, c.y - 0.06, c.z,
    ).castShadow = true;

    const waterMat = new THREE.MeshStandardMaterial({
      map: waterTexture(), color: 0xbfe9ff, roughness: 0.25, metalness: 0.05,
      transparent: true, opacity: 0.92,
    });
    this._disposables.push(waterMat);
    this.water = this._decor(
      new THREE.RingGeometry(inner, outer, 40, 1), waterMat, c.x, c.y, c.z,
    );
    this.water.rotation.x = -Math.PI / 2;

    this._decor(
      new THREE.TorusGeometry(outer + 0.04, 0.03, 8, 30),
      factory.mat(PALETTE.stripeA, { rough: 0.5 }),
      c.x, c.y + 0.04, c.z,
    ).rotation.x = -Math.PI / 2;

    // Plinth: the one solid thing in the booth, so nothing dropped falls
    // through the floor.
    const plinthGeo = new THREE.BoxGeometry(outer * 2.3, c.y - 0.28, outer * 2.3);
    this._disposables.push(plinthGeo);
    const plinth = new THREE.Mesh(plinthGeo, factory.mat(PALETTE.wood, { rough: 0.9 }));
    plinth.receiveShadow = true;
    this.spawn(plinth, { x: c.x, y: (c.y - 0.28) / 2, z: c.z }, {
      type: 'fixed', shape: 'box',
      halfExtents: { x: outer * 1.15, y: (c.y - 0.28) / 2, z: outer * 1.15 },
      position: this.localToWorld(c.x, (c.y - 0.28) / 2, c.z),
      friction: 0.8,
    });

    this._buildRodHolder();

    const sign = factory.createSign('', {
      w: 1.5, h: 0.42,
      lines: ['LARGE 50   SMALL 200   GOLDEN 500'],
      bg: '#12172a',
    });
    sign.position.set(0, 1.9, c.z + 0.85);
    sign.rotation.y = Math.PI;
    this.root.add(sign);

    this.buildScoreboard({ y: 2.45, z: c.z + 0.9 });

    const light = createStallLight(0xcfe9ff, 2.2, 9);
    light.position.set(0, 2.6, c.z);
    this.root.add(light);
  }

  /**
   * The counter the rod waits on, plus the two pegs it rests across. The
   * counter is load-bearing visually as well as physically — without it the
   * pegs hang in mid-air half a metre clear of the basin.
   * @private
   */
  _buildRodHolder() {
    const r = CFG.rodRest;
    this.buildTray({ width: 0.9, y: 1.0, z: r.z });
    for (const dz of [-0.1, 0.12]) {
      const geometry = new THREE.CylinderGeometry(0.018, 0.022, 0.14, 8);
      const peg = new THREE.Mesh(
        geometry,
        factory.mat(PALETTE.metal, { rough: 0.4, metal: 0.7 }),
      );
      peg.position.set(r.x, 1.1, r.z + dz);
      this.root.add(peg);
      this._disposables.push(geometry);
    }
  }

  // ── Round setup ─────────────────────────────────────────────────────────

  setupRound() {
    this.setRoundFormat({ seconds: CFG.seconds });
    this.catches = 0;
    this.fishAwarded = [];
    this._elapsed = 0;
    this._tipVelY = 0;
    this._prevTip = null;
    this._rodReturn = 0;
    this._effects = [];
    this.ducks = [];

    this._spawnRod();
    this._spawnDucks();
    this._initChain(this._rodTipLocal());
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'water-loop', loop: true, volume: 0.4 });
  }

  /** @private */
  _spawnRod() {
    const rest = CFG.rodRest;
    const mesh = factory.createFishingRod();
    this.rodMesh = mesh;

    // The factory line has two points; the chain needs one per node.
    const line = mesh.userData.line;
    const pts = [];
    for (let i = 0; i <= CFG.lineSegments; i++) pts.push(new THREE.Vector3());
    line.geometry.dispose();
    line.geometry = new THREE.BufferGeometry().setFromPoints(pts);
    line.frustumCulled = false;

    this.rod = this.spawn(mesh, rest, {
      type: 'kinematic', shape: 'box',
      halfExtents: { x: 0.03, y: 0.03, z: 0.09 },
      mass: 0.35, friction: 0.6,
      position: this.localToWorld(rest.x, rest.y, rest.z),
      rotation: this._restQuaternion(),
    });
    this.rod.userData.rod = true;

    grab.register(this.rod, {
      kind: 'fishing-rod',
      holdOffset: mesh.userData.holdOffset,
      onRelease: () => this._onRodReleased(),
    });
  }

  /** @returns {THREE.Quaternion} Rod pose in the holder: level, into the booth. */
  _restQuaternion() {
    // The rod model points down its own -Z, so a yaw of pi aims it at local +Z.
    _e.set(-0.12, Math.PI, 0);
    return this.worldQuaternion().multiply(_q.setFromEuler(_e));
  }

  /** @private */
  _onRodReleased() {
    // A released rod is not a thrown object — it goes back on its pegs. Killing
    // the body type here beats the velocity grab.release() has just applied.
    if (!this.rod || this.rod.removed) return;
    this.rod.setKinematic(true);
    this.rod.freeze();
    this._rodReturn = CFG.rodReturnDelay + CFG.rodReturnTime;
  }

  /** @private */
  _spawnDucks() {
    /** @type {object[]} */
    const defs = [];
    for (const d of CFG.ducks) for (let i = 0; i < d.count; i++) defs.push(d);

    for (let i = 0; i < defs.length; i++) {
      const def = defs[i];
      const angle = (i / defs.length) * Math.PI * 2 + randRange(-0.1, 0.1);
      const mesh = factory.createRubberDuck(def.size);
      const p = this._ringPosition(angle, 0, 0);

      const handle = this.spawn(mesh, p, {
        type: 'kinematic', shape: 'ball', radius: 0.06,
        groups: GHOST,
        position: this.localToWorld(p.x, p.y, p.z),
      });
      const id = this.registerTarget(handle, { group: def.size, id: `duck-${i}` });

      this.ducks.push({
        id,
        def,
        handle,
        mesh,
        angle,
        yaw: Math.PI - angle,
        phase: randRange(0, Math.PI * 2),
        ringRadius: mesh.userData.ringRadius,
        state: 'floating',
        timer: 0,
        local: p,
      });
    }
  }

  // ── Geometry helpers ────────────────────────────────────────────────────

  /**
   * A point on the duck path.
   * @param {number} angle @param {number} t Elapsed seconds. @param {number} phase
   * @returns {{x: number, y: number, z: number}} Stall-local.
   */
  _ringPosition(angle, t, phase) {
    const c = CFG.ringCentre;
    return {
      x: c.x + Math.cos(angle) * CFG.ringRadius,
      y: c.y - 0.02 + Math.sin(t * CFG.bobFrequency + phase) * CFG.bobAmplitude,
      z: c.z + Math.sin(angle) * CFG.ringRadius,
    };
  }

  /**
   * Where a duck's head ring sits, in stall-local space.
   * @param {object} duck
   * @returns {THREE.Vector3} A shared scratch vector — copy it if you keep it.
   */
  _ringAnchorLocal(duck) {
    const p = duck.local;
    _e.set(0, duck.yaw, 0);
    _v2.set(0, 0.14, -0.045).applyQuaternion(_q.setFromEuler(_e));
    return _v.set(p.x + _v2.x, p.y + _v2.y, p.z + _v2.z);
  }

  /** @returns {THREE.Vector3} Rod tip in stall-local space. */
  _rodTipLocal() {
    if (!this.rodMesh) return new THREE.Vector3(0, 1.3, 0.5);
    this.rodMesh.updateWorldMatrix(true, false);
    this.rodMesh.userData.tip.getWorldPosition(_v2);
    return this.worldToLocal(_v2);
  }

  // ── The line ────────────────────────────────────────────────────────────

  /** @param {THREE.Vector3} anchor @private */
  _initChain(anchor) {
    const seg = CFG.lineLength / CFG.lineSegments;
    this.chain = [];
    for (let i = 0; i <= CFG.lineSegments; i++) {
      const p = anchor.clone();
      p.y -= seg * i;
      this.chain.push({ pos: p, prev: p.clone() });
    }
  }

  /**
   * One Verlet step plus distance relaxation. The anchor node is pinned to the
   * rod tip, so the hook lags and swings exactly as a real line would.
   * @param {THREE.Vector3} anchor @param {number} dt
   * @private
   */
  _stepChain(anchor, dt) {
    const seg = CFG.lineLength / CFG.lineSegments;
    const gravity = -DEFAULT_GRAVITY * dt * dt;

    for (let i = 1; i < this.chain.length; i++) {
      const n = this.chain[i];
      const vx = (n.pos.x - n.prev.x) * CFG.lineDamping;
      const vy = (n.pos.y - n.prev.y) * CFG.lineDamping;
      const vz = (n.pos.z - n.prev.z) * CFG.lineDamping;
      n.prev.copy(n.pos);
      n.pos.set(n.pos.x + vx, n.pos.y + vy + gravity, n.pos.z + vz);
    }
    this.chain[0].prev.copy(this.chain[0].pos);
    this.chain[0].pos.copy(anchor);

    for (let k = 0; k < CFG.lineIterations; k++) {
      for (let i = 1; i < this.chain.length; i++) {
        const a = this.chain[i - 1];
        const b = this.chain[i];
        _v2.subVectors(b.pos, a.pos);
        const len = _v2.length() || 1e-6;
        const corr = (len - seg) / len;
        if (i === 1) {
          b.pos.addScaledVector(_v2, -corr);
        } else {
          a.pos.addScaledVector(_v2, corr * 0.5);
          b.pos.addScaledVector(_v2, -corr * 0.5);
        }
      }
    }
  }

  /** @returns {THREE.Vector3} The hook node, stall-local. */
  _hookLocal() {
    return this.chain[this.chain.length - 1].pos;
  }

  /**
   * Ring anchor of the floating duck whose ring is nearest the hook.
   * @returns {THREE.Vector3|null} A fresh vector, stall-local.
   * @private
   */
  _nearestRing() {
    if (this.chain.length === 0) return null;
    const hook = this._hookLocal();
    let best = null;
    let bestD = Infinity;
    for (const duck of this.ducks) {
      if (duck.state !== 'floating' || !duck.local) continue;
      const a = this._ringAnchorLocal(duck);
      const d = (a.x - hook.x) ** 2 + (a.z - hook.z) ** 2;
      if (d < bestD) {
        bestD = d;
        best = a.clone();
      }
    }
    return best;
  }

  /** Push the chain onto the rod's own line and hook meshes. @private */
  _renderLine() {
    if (!this.rodMesh) return;
    const line = this.rodMesh.userData.line;
    const hook = this.rodMesh.userData.hook;
    const array = line.geometry.attributes.position.array;

    this.rodMesh.updateWorldMatrix(true, false);
    for (let i = 0; i < this.chain.length; i++) {
      const world = this.localToWorld(
        this.chain[i].pos.x, this.chain[i].pos.y, this.chain[i].pos.z,
      );
      this.rodMesh.worldToLocal(world);
      array[i * 3] = world.x;
      array[i * 3 + 1] = world.y;
      array[i * 3 + 2] = world.z;
    }
    line.geometry.attributes.position.needsUpdate = true;
    hook.position.set(
      array[(this.chain.length - 1) * 3],
      array[(this.chain.length - 1) * 3 + 1],
      array[(this.chain.length - 1) * 3 + 2],
    );
  }

  // ── Per-frame ───────────────────────────────────────────────────────────

  onUpdate(dt) {
    // The manager assigns itself the active stall BEFORE awaiting the
    // first-person blink, and StallBase._update calls onUpdate regardless of
    // roundActive — so this runs for the ~50 frames of that transition, before
    // setupRound() has built the rod or the line. Nothing here means anything
    // until the chain exists.
    if (this.chain.length === 0) return;

    const step = THREE.MathUtils.clamp(dt, 1 / 120, 1 / 30);
    this._elapsed += dt;

    this._updateRod(dt);

    const tip = this._rodTipLocal();
    if (this._prevTip) {
      // Exponential smoothing: one jittery frame must not read as a lift.
      const raw = (tip.y - this._prevTip.y) / step;
      this._tipVelY += (raw - this._tipVelY) * 0.35;
    }
    this._prevTip = tip.clone();

    this._stepChain(tip, step);
    this._renderLine();

    if (this.water) this.water.rotation.z -= (CFG.currentSpeed / CFG.ringRadius) * dt;

    this._updateDucks(dt);
    this._effects = this._effects.filter((fn) => fn(dt));
  }

  /** @private */
  _updateRod(dt) {
    if (!this.rod || this.rod.removed) return;
    if (grab.heldBy('right') === this.rod || grab.heldBy('left') === this.rod) {
      this._rodReturn = 0;
      return;
    }
    if (this._rodReturn <= 0) return;

    this._rodReturn -= dt;
    if (this._rodReturn > CFG.rodReturnTime) return;

    const k = THREE.MathUtils.clamp(1 - this._rodReturn / CFG.rodReturnTime, 0, 1);
    const r = CFG.rodRest;
    const target = this.localToWorld(r.x, r.y, r.z);
    _v2.copy(this.rod.position()).lerp(target, k);
    this.rod.setPosition(_v2);
    this.rod.setRotation(this._restQuaternion());
  }

  /** @private */
  _updateDucks(dt) {
    const angular = CFG.currentSpeed / CFG.ringRadius;

    for (const duck of this.ducks) {
      if (duck.state === 'caught') {
        this._rideHook(duck, dt);
        continue;
      }

      duck.angle = (duck.angle + angular * dt) % (Math.PI * 2);
      duck.yaw = Math.PI - duck.angle;
      duck.local = this._ringPosition(duck.angle, this._elapsed, duck.phase);
      this._placeDuck(duck);
      this._tryCatch(duck);
    }
  }

  /** Write a duck's local pose onto its kinematic body. @private */
  _placeDuck(duck) {
    const p = duck.local;
    duck.handle.setPosition(this.localToWorld(p.x, p.y, p.z));
    _e.set(0, duck.yaw, 0);
    duck.handle.setRotation(this.worldQuaternion().multiply(_q.setFromEuler(_e)));
  }

  // ── Catching ────────────────────────────────────────────────────────────

  /**
   * The catch rule (GDD 6.7.1): the hook must be inside the ring — horizontally
   * within its radius and vertically through it — while the rod is on the way
   * up. Standing the hook in the ring does nothing; you have to lift.
   *
   * @param {object} duck
   * @returns {boolean} True if this call caught the duck.
   * @private
   */
  _tryCatch(duck) {
    if (!this.roundActive || duck.state !== 'floating') return false;
    if (this._tipVelY < CFG.liftSpeed) return false;

    const hook = this._hookLocal();
    const ax = hook.x;
    const ay = hook.y;
    const az = hook.z;
    const anchor = this._ringAnchorLocal(duck);

    const rise = ay - anchor.y;
    if (rise > CFG.catchHeightWindow || rise < -CFG.catchDepthWindow) return false;
    const dx = ax - anchor.x;
    const dz = az - anchor.z;
    const r = duck.ringRadius + CFG.hookTolerance;
    if (dx * dx + dz * dz > r * r) return false;

    this._catch(duck);
    return true;
  }

  /** @private */
  _catch(duck) {
    duck.state = 'caught';
    duck.timer = CFG.liftTime;
    duck.handle.userData.scored = true;
    this.catches += 1;

    const world = this.localToWorld(duck.local.x, duck.local.y, duck.local.z);
    this.addScore(duck.def.points, {
      position: world,
      label: `${duck.def.size} duck`,
      color: duck.def.size === 'golden' ? '#ffd700' : '#ffd166',
    });
    this._splash(duck.local);
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'water-splash', volume: 0.7 });
    haptics.play('right', 'score');
  }

  /** A caught duck hangs off the hook while it is lifted clear. @private */
  _rideHook(duck, dt) {
    duck.timer -= dt;
    const hook = this._hookLocal();
    _e.set(0, duck.yaw, 0);
    _v2.set(0, 0.14, -0.045).applyQuaternion(_q.setFromEuler(_e));
    duck.local = { x: hook.x - _v2.x, y: hook.y - _v2.y, z: hook.z - _v2.z };
    this._placeDuck(duck);

    if (duck.timer <= 0) this._respawn(duck);
  }

  /** Drop the duck back into the current on the far side of the ring. @private */
  _respawn(duck) {
    duck.state = 'floating';
    duck.angle = (duck.angle + Math.PI) % (Math.PI * 2);
    duck.yaw = Math.PI - duck.angle;
    duck.handle.userData.scored = false;
    duck.local = this._ringPosition(duck.angle, this._elapsed, duck.phase);
    this._placeDuck(duck);
    this._splash(duck.local);
  }

  /** Expanding ring on the water. @private */
  _splash(local) {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.02, 0.05, 16),
      new THREE.MeshBasicMaterial({
        color: 0xe6f7ff, transparent: true, opacity: 0.8, side: THREE.DoubleSide,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(local.x, CFG.ringCentre.y + 0.012, local.z);
    this.root.add(ring);

    let t = 0;
    this._effects.push((dt) => {
      t += dt;
      const k = t / 0.55;
      ring.scale.setScalar(1 + k * 3.2);
      ring.material.opacity = Math.max(0, 0.8 * (1 - k));
      if (k < 1) return true;
      this.root.remove(ring);
      ring.geometry.dispose();
      ring.material.dispose();
      return false;
    });
  }

  // ── Round end ───────────────────────────────────────────────────────────

  /**
   * GDD 7.4: a fish every round no matter what, plus one per three catches.
   * This is the guaranteed-reward stall — zero catches must still pay out.
   */
  onRoundEnd() {
    const count = CFG.guaranteedFish
      + Math.floor(this.catches / CFG.catchesPerBonusFish);

    for (let i = 0; i < count; i++) {
      const variant = this._rollFishVariant();
      this.awardDirectPrize(PRIZE_KIND.FISH, { variant });
      this.fishAwarded.push(variant);
      this._showFish(variant, i, count);
    }
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'prize-earned' });
  }

  /** @returns {string} A weighted variant id (golden ≈ 10%). @private */
  _rollFishVariant() {
    let total = 0;
    for (const v of CFG.fishVariants) total += v.weight;
    let roll = rng() * total;
    for (const v of CFG.fishVariants) {
      roll -= v.weight;
      if (roll < 0) return v.id;
    }
    return CFG.fishVariants[0].id;
  }

  /**
   * Float the won fish over the water so the payout is visible. Placed at full
   * size rather than animated in: this runs from `onRoundEnd`, after the last
   * `onUpdate` of the round, so no frame hook is left to grow it.
   * @private
   */
  _showFish(variant, index, count) {
    const fish = factory.createFish(variant);
    const c = CFG.ringCentre;
    const spread = (index - (count - 1) / 2) * 0.16;
    fish.position.set(c.x + spread, c.y + 0.2, c.z);
    fish.rotation.z = 0.3;
    this.root.add(fish);
  }

  // ── Scoreboard ──────────────────────────────────────────────────────────

  updateScoreboard() {
    if (!this.scoreboard) return;
    const bonus = Math.floor(this.catches / CFG.catchesPerBonusFish);
    this.scoreboard.setLines([
      `Score   ${this.score}`,
      `Time    ${Math.ceil(Math.max(0, this.timeLeft))}s`,
      `Ducks   ${this.catches}`,
      `Fish    ${CFG.guaranteedFish + bonus}`,
    ]);
  }

  onTeardown() {
    if (this.rod) grab.unregister(this.rod);
    this.rod = null;
    this.rodMesh = null;
    this.water = null;
    this.ducks = [];
    this.chain = [];
    this._effects = [];
    this._prevTip = null;
    for (const d of this._disposables) d.dispose();
    this._disposables = [];
  }

  // ── Test automation (reached through __cc.stalls, never imported) ────────

  /**
   * Snapshot for `__cc.stalls.debug()`.
   * @returns {object}
   */
  debugState() {
    const nearest = this._nearestRing();
    return {
      stallId: this.id,
      catches: this.catches,
      fishAwarded: [...this.fishAwarded],
      tipVelocityY: this._tipVelY,
      hook: this._hookLocal().toArray(),
      rodHeld: !!this.rod
        && (grab.heldBy('right') === this.rod || grab.heldBy('left') === this.rod),
      /** Ring anchor of the floating duck nearest the hook, stall-local. */
      nearestRing: nearest ? nearest.toArray() : null,
      ducks: this.ducks.map((d) => ({
        id: d.id, size: d.def.size, points: d.def.points,
        state: d.state, ringRadius: d.ringRadius,
      })),
    };
  }

  /**
   * Stall-specific test hooks, routed from `__cc.stalls.action()`.
   * @param {string} name @param {object} args
   */
  testHook(name, args = {}) {
    if (name === 'hookAt') return this.hookAt(args.duckId);
    return null;
  }

  /**
   * Place the hook on a duck's ring and lift, then run the ordinary catch
   * check. Backs `__cc.stalls.hookAt(duckId)`.
   *
   * This stages the pose a player would have to produce by hand; the rule that
   * decides whether it scores is the same `_tryCatch` the frame loop uses.
   *
   * @param {string} duckId
   * @returns {boolean} True if the duck was caught.
   */
  hookAt(duckId) {
    const duck = this.ducks.find((d) => d.id === duckId);
    if (!duck || duck.state !== 'floating' || !this.roundActive) return false;

    const anchor = this._ringAnchorLocal(duck).clone();
    for (const node of this.chain) {
      node.pos.copy(anchor);
      node.prev.copy(anchor);
    }
    this._tipVelY = CFG.liftSpeed * 2;
    return this._tryCatch(duck);
  }
}

registerStall({
  id: 'hook-a-duck',
  name: 'Hook a Duck',
  cost: CFG.cost,
  create: (ctx) => new HookADuck(ctx),
});

export { HookADuck };
